import React, { useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import { getCancelationConstantsRequest, updateAppConstants } from '../../../../store/Settings/actions'
import { ROUTE_PATHS } from '../../../../utils/RoutesPaths';
import { setPageMainTitle } from '../../../../utils/Helpers';
import { POSITIVE_FLOAT_REGEX, FROM_ZERO_TO_INFINITY } from '../../../../utils/Patterns'
import messages from "../../../../assets/locale/messages";
import Breadcrumbs from '../../../../components/BreadCrumbs';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Switch from '../../../../components/Switch';
import Tooltip from '../../../../components/Tooltip'

const EditCancellationPolicy = () => {

    const dispatch = useDispatch()
    const history = useHistory();
    const lang = useSelector((state) => state.locale.lang);
    const cancellationPolicyConstants = useSelector((state) => state.settings.cancellationPolicyConstants);
    const { settings, shared } = messages[lang];

    const {values,
        handleSubmit,
        setFieldTouched,
        setFieldValue,
        handleChange,
        touched, 
        errors
       } = useFormik({
                initialValues: {
                    hours: null,
                    isAllowedToCancel: true,
                    maxNumber: null,
                    days: null
                },
                validationSchema: Yup.object({
                                    hours: Yup.number().typeError('numbersOnly')
                                        .test(
                                          "is-decimal",
                                          "startingFromZero",
                                          (val) => {
                                               if (val != undefined) {
                                                   return POSITIVE_FLOAT_REGEX.test(val);
                                               }
                                               return true;
                                           }),
                                    maxNumber: Yup.number().typeError('numbersOnly').when('isAllowedToCancel',{
                                        is:isAllowedToCancel => isAllowedToCancel === false,
                                        then:Yup.number().typeError('numbersOnly').required('required').test(
                                            "is-decimal",
                                            "integerNumber",
                                            (val) => {
                                                 if (val != undefined) {
                                                     return FROM_ZERO_TO_INFINITY.test(val);
                                                 }
                                                 return true;
                                             }).min(2,'numbersStartingFrom2')
                                    }),
                                    days: Yup.number().typeError('numbersOnly').when('isAllowedToCancel',{
                                        is:isAllowedToCancel => isAllowedToCancel === false,
                                        then:Yup.number().typeError('numbersOnly').required('required').test(
                                            "is-decimal",
                                            "zeroAndPositiveNumber",
                                            (val) => {
                                                 if (val != undefined) {
                                                     return FROM_ZERO_TO_INFINITY.test(val);
                                                 }
                                                 return true;
                                             }),
                                    }),
                }),
                onSubmit: async ({hours, isAllowedToCancel, maxNumber, days}) => {
                    const params = {
                        cancellation_cutoff_hours: +hours,
                        allow_consecutive_cancellations: isAllowedToCancel,
                        max_consecutive_cancellations: isAllowedToCancel ? null : +maxNumber,
                        cancellation_prevention_days: isAllowedToCancel ? null : +days 
                    };
                    dispatch(updateAppConstants({
                        data: params,
                        from:'cancellationPolicy'
                    }));
                }
    });

    useEffect(() => {
        setPageMainTitle('SettingsManagment');
        dispatch(getCancelationConstantsRequest());
    }, []);

    useEffect(() => {
        if(Object.keys(cancellationPolicyConstants).length){
            setFieldValue( "hours" , cancellationPolicyConstants.cancellationCutoffHours)
            setFieldValue("isAllowedToCancel" , cancellationPolicyConstants.allowConsecutiveCancellations)
            setFieldValue("days" , cancellationPolicyConstants.cancellationPreventionDays)
            setFieldValue("maxNumber" , cancellationPolicyConstants.maxConsecutiveCancellations )
        }
    },[cancellationPolicyConstants])

    return(
        <div>
            <Breadcrumbs
                list={[
                    {
                        path: ROUTE_PATHS.cancellationPolicy,
                        label: settings.viewSettings.cancellationPolicy
                    },
                    {
                        path: "#",
                        label: shared.edit
                    }
                ]}
                className="fsize-28 fweight-600 mb-4" 
            />
            <form onSubmit={handleSubmit}>
                <div className="bg-white rounded-4 px-5 py-4 mb-4  fweight-400">
                    <div className="text-secondary mb-3 fsize-18">
                        {settings.cancellationPolicy.cancellationDuration}:
                    </div>
                    <div className="mb-3 fsize-16">
                        {settings.cancellationPolicy.cancelationDurationlabel}
                    </div>
                    <Input
                        id="hours"
                        name="hours"
                        type="text"
                        placeholder={settings.cancellationPolicy.hoursPlaceholders}
                        value={values["hours"]}
                        onChange={(value) => {
                            setFieldTouched("hours");
                            setFieldValue("hours", value);
                          }}
                        isInputHasErr={!!(touched["hours"] && errors["hours"])}
                        errMsg={errors["hours"]}
                    />
                </div>
                <div className="bg-white rounded-4 px-5 py-4 mb-4 fsize-18 fweight-400">
                    <div className="text-secondary mb-3">
                        {settings.cancellationPolicy.consecutiveCancellations}:
                    </div>
                    <div className="d-flex gap-3 mb-3">
                        <div className="mb-1 text-gray">
                            {settings.cancellationPolicy.consecutiveCancellationsText}
                        </div>
                        <Switch
                            id="isAllowedToCancel"
                            checked={values["isAllowedToCancel"]}
                            onChange={handleChange}
                        />
                    </div>
                    {!values.isAllowedToCancel && (
                        <div>
                            <div className="fsize-16 fweight-400 mb-3">
                                {settings.cancellationPolicy.maxNumberOfConsecutiveCancellations}* 
                                <Tooltip 
                                    title={settings.cancellationPolicy.hint} 
                                    content={<span className="mx-1"><ErrorOutlineOutlinedIcon color="secondary"/></span>}
                                />
                            </div>
                            
                            <Input
                                id="maxNumber"
                                name="maxNumber"
                                type="text"
                                inputWrapperClass="mb-3"
                                placeholder={settings.cancellationPolicy.maxNumberPlaceholder}
                                value={values["maxNumber"]}
                                onChange={(value) => {
                                    setFieldTouched("maxNumber");
                                    setFieldValue("maxNumber", value);
                                }}
                                isInputHasErr={!!(touched["maxNumber"] && errors["maxNumber"])}
                                errMsg={errors["maxNumber"]}
                            />
                            <div className="fsize-16 fweight-400 mb-3">
                                {settings.cancellationPolicy.numberOfDaysForPreventingFurtherCancelllations}*
                            </div>
                            <Input
                                id="days"
                                name="days"
                                type="text"
                                placeholder={settings.cancellationPolicy.daysPlaceholder}
                                value={values["days"]}
                                onChange={(value) => {
                                    setFieldTouched("days");
                                    setFieldValue("days", value);
                                }}
                                isInputHasErr={!!(touched["days"] && errors["days"])}
                                errMsg={errors["days"]}
                            />
                        </div>)
                    }
                </div>   
                <div className= "d-flex justify-content-end gap-2">
                    <Button
                        label={shared.cancel}
                        labelClass="text-secondary px-3"
                        outlined
                        onClick={()=> {history.push(ROUTE_PATHS.cancellationPolicy)}}
                    />
                    <Button
                        type="sumbit"
                        label={shared.save}
                        labelClass="text-white px-3"
                        btnStyle="secondary"
                        onClick={()=> {handleSubmit()}}
                        disabled={!!(errors["maxNumber"] || errors["days"] || errors["hours"])}
                    />
                </div>
            </form>
        </div>
    );
}

export default EditCancellationPolicy;